import React, { useRef, useState, useEffect } from 'react';
import Image from 'next/image';
import { PlayCircleFilled } from '@ant-design/icons';

// 视频卡片数据类型
export interface VideoCardData {
  id: string;
  username: string;
  userHandle: string;
  videoUrl: string;
  thumbnailUrl: string;
  link: string;
}

interface VideoCardProps {
  data: VideoCardData;
  platform: 'tiktok' | 'instagram';
}

const VideoCard: React.FC<VideoCardProps> = ({ data, platform }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const cardId = `${platform}-${data.id}`;
  const [isHovering, setIsHovering] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const notifyVideoPlay = () => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(
      new CustomEvent('video-card-play', {
        detail: { id: cardId },
      })
    );
  };

  const playVideo = () => {
    if (!videoRef.current) return;
    notifyVideoPlay();
    videoRef.current
      .play()
      .then(() => {
        setIsPlaying(true);
      })
      .catch(() => {
        // 忽略自动播放失败的错误
      });
  };

  const resetVideo = () => {
    if (!videoRef.current) return;
    videoRef.current.pause();
    videoRef.current.currentTime = 0;
    setIsPlaying(false);
  };

  // 鼠标悬停时播放视频
  const handleMouseEnter = () => {
    setIsHovering(true);
    playVideo();
  };

  // 鼠标移开时暂停视频并重置
  const handleMouseLeave = () => {
    setIsHovering(false);
    resetVideo();
  };

  // 点击视频区域时播放
  const handleVideoClick = () => {
    playVideo();
  };

  // 点击用户名跳转到对应链接
  const handleUsernameClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    window.open(data.link, '_blank');
  };

  // 清理：组件卸载时停止视频
  useEffect(() => {
    const handleExternalVideoPlay = (event: Event) => {
      const customEvent = event as CustomEvent<{ id: string }>;
      if (customEvent.detail?.id !== cardId) {
        resetVideo();
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('video-card-play', handleExternalVideoPlay);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('video-card-play', handleExternalVideoPlay);
      }
      if (videoRef.current) {
        videoRef.current.pause();
      }
    };
  }, [cardId]);

  return (
    <div
      className="min-w-[240px] max-w-[280px] max-md:min-w-[200px] max-md:max-w-[220px] bg-white rounded-2xl overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_24px_rgba(0,0,0,0.15)] flex flex-col"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-video-card="true"
    >
      {/* 视频容器 */}
      <div className="relative w-full aspect-[9/16] bg-black overflow-hidden cursor-pointer" onClick={handleVideoClick}>
        {/* 视频元素 */}
        <video ref={videoRef} className="w-full h-full object-cover" loop muted playsInline poster={data.thumbnailUrl}>
          <source src={data.videoUrl} type="video/mp4" />
        </video>

        {/* 缩略图覆盖层（视频未播放时显示） */}
        {!isPlaying && (
          <div className="absolute top-0 left-0 w-full h-full z-[1]">
            <Image src={data.thumbnailUrl} alt={data.username} fill className="object-cover" />
          </div>
        )}

        {/* 播放按钮图标（未播放时显示） */}
        {!isPlaying && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[2] text-[64px] max-md:text-[48px] text-white/90 transition-all duration-300 hover:text-white hover:scale-110 pointer-events-none">
            <PlayCircleFilled />
          </div>
        )}

        {/* 悬停遮罩 */}
        {isHovering && <div className="absolute top-0 left-0 w-full h-full bg-black/10 z-[1] pointer-events-none" />}
      </div>

      {/* 底部用户信息 */}
      <div className="flex items-center gap-2.5 p-3 max-md:p-2.5 bg-white">
        <Image
          src={platform === 'tiktok' ? '/images/testimonials/tiktoktag.svg' : '/images/testimonials/instagramtag.svg'}
          alt={platform}
          width={20}
          height={20}
          className="flex-shrink-0 max-md:w-4 max-md:h-4"
        />
        <button
          type="button"
          className="text-sm max-md:text-[13px] font-medium text-[#333333] whitespace-nowrap overflow-hidden text-ellipsis flex-1 text-left hover:text-[var(--app-primary-color,#725CFF)] transition-colors duration-200"
          onClick={handleUsernameClick}
        >
          {data.username}
        </button>
      </div>
    </div>
  );
};

export default VideoCard;
